import {
  CAROUSEL_DEFAULT_STYLE_KEY,
  applyCarouselStyle,
  carouselStyleByKey,
  carouselStylePatch,
} from './carouselStyles';

/**
 * Props par défaut d'une slide — SOURCE UNIQUE : la démo de la config Puck, le
 * bouton « Ajouter une slide » et la macro Bandeau de logos. Une slide posée
 * sans toutes ses props affiche des champs vides en sidebar alors que le rendu
 * applique bien ses défauts.
 * Fonction (pas constante) : chaque slide doit être un objet distinct.
 */
export function carouselSlideDefaults(overrides = {}) {
  return {
    title: '',
    description: '',
    backgroundMedia: '',
    contentMedia: '',
    slideBackground: '',
    ctaEnabled: false,
    ctaLabel: 'En savoir plus',
    ctaUrl: '',
    ctaColor: '',
    ...overrides,
  };
}

/**
 * Le bouton d'une slide, sous la forme que `resolveButtonStyle` et la galerie
 * de styles attendent — la slide préfixe ses props par `cta`.
 *
 * Ni `ctaBorder` ni `ctaButtonStyle` ne figurent dans `carouselSlideDefaults` :
 * une slide déjà en base n'en a pas, et la résolution les reconstruit à
 * l'identique de ce qu'elle affiche aujourd'hui (moyen, pilule, sans trait).
 */
export function slideButtonProps(slide = {}) {
  return {
    color: slide.ctaColor || undefined,
    border: slide.ctaBorder,
    buttonStyle: slide.ctaButtonStyle,
  };
}

/**
 * Props par défaut du bloc — même rôle de source unique. Les réglages viennent
 * du style « Diaporama » : un carrousel fraîchement posé correspond donc à une
 * vignette du catalogue par construction, jamais à « Personnalisé ».
 */
export function carouselDefaultProps() {
  return {
    // Démo au drop : l'utilisateur voit le rendu et remplace par ses données.
    slides: [
      carouselSlideDefaults({
        title: 'Votre message principal',
        description: 'Une phrase pour appuyer le message.',
        slideBackground: '#f1f3f5',
      }),
      carouselSlideDefaults({
        title: 'Un second temps fort',
        description: 'Ajoutez un média depuis les propriétés de la slide.',
        slideBackground: '#e7f5ff',
      }),
    ],
    ...carouselStylePatch(carouselStyleByKey(CAROUSEL_DEFAULT_STYLE_KEY)),
  };
}

/** Marques d'illustration : des formes, JAMAIS un nom de société. */
const DEMO_MARKS = {
  // Deux disques qui se recouvrent.
  pair: "%3Ccircle cx='12' cy='24' r='11' fill='%23ced4da'/%3E%3Ccircle cx='22' cy='24' r='11' fill='%23dee2e6'/%3E",
  // Hexagone.
  hexagon: "%3Cpath d='M16 8 L29 16 L29 32 L16 40 L3 32 L3 16 Z' fill='%23ced4da'/%3E",
  // Quatre carrés.
  grid: "%3Crect x='3' y='11' width='12' height='12' rx='3' fill='%23ced4da'/%3E%3Crect x='18' y='11' width='12' height='12' rx='3' fill='%23dee2e6'/%3E%3Crect x='3' y='26' width='12' height='12' rx='3' fill='%23dee2e6'/%3E%3Crect x='18' y='26' width='12' height='12' rx='3' fill='%23ced4da'/%3E",
  // Chevron.
  chevron: "%3Cpath d='M4 10 L18 24 L4 38 L12 38 L26 24 L12 10 Z' fill='%23ced4da'/%3E",
  // Barres montantes.
  bars: "%3Crect x='3' y='28' width='7' height='10' rx='2' fill='%23dee2e6'/%3E%3Crect x='13' y='20' width='7' height='18' rx='2' fill='%23ced4da'/%3E%3Crect x='23' y='11' width='7' height='27' rx='2' fill='%23adb5bd'/%3E",
  // Goutte (carré à un angle rond).
  drop: "%3Cpath d='M16 8 L30 22 A14 14 0 1 1 16 8 Z' fill='%23ced4da'/%3E",
};
/**
 * Une identité par slide : marque, police, graisse, casse. Six logos qui se
 * ressemblent tous ne donnent pas l'impression de six clients — c'est le
 * mélange de polices qui fait le bandeau crédible.
 * `charWidth` : largeur moyenne d'un caractère dans cette police, à cette
 * taille. Elle sert à dimensionner le SVG au plus juste — trop large, il
 * laisserait du vide transparent et le ruban aurait des trous.
 */
const DEMO_LOGO_STYLES = [
  { mark: 'pair', font: 'Trebuchet MS, sans-serif', size: 19, weight: 700, spacing: -0.4, charWidth: 10 },
  { mark: 'hexagon', font: 'Verdana, sans-serif', size: 15, weight: 700, spacing: 0.8, upper: true, charWidth: 12 },
  { mark: 'grid', font: 'Futura, Century Gothic, sans-serif', size: 18, weight: 400, spacing: 3, upper: true, charWidth: 13 },
  { mark: 'chevron', font: 'Impact, Haettenschweiler, sans-serif', size: 20, weight: 400, spacing: 0.4, upper: true, charWidth: 10 },
  { mark: 'bars', font: 'Times New Roman, serif', size: 20, weight: 700, spacing: 0, charWidth: 10 },
  { mark: 'drop', font: 'Optima, Segoe UI, sans-serif', size: 18, weight: 400, spacing: 1.2, italic: true, charWidth: 10 },
];

/**
 * Logo d'illustration : une marque + un nom, en SVG inline. Un `data:` URI
 * évite un asset à servir, et `normalizeMediaSource` le traite comme une image
 * ordinaire — l'utilisateur remplace chaque slide par le logo de son client.
 * Les largeurs diffèrent d'un logo à l'autre : c'est ce qui montre qu'un ruban
 * accepte des proportions libres.
 */
function demoLogo(style, index) {
  const label = style.upper ? `CLIENT ${index + 1}` : `Client ${index + 1}`;
  const textWidth = Math.round(label.length * style.charWidth + label.length * style.spacing);
  const width = 48 + textWidth;
  const italic = style.italic ? " font-style='italic'" : '';
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='48' viewBox='0 0 ${width} 48'%3E${DEMO_MARKS[style.mark]}%3Ctext x='40' y='31' font-family='${style.font}' font-size='${style.size}' font-weight='${style.weight}' letter-spacing='${style.spacing}'${italic} fill='%23868e96'%3E${label}%3C/text%3E%3C/svg%3E`;
}

/**
 * Contenu de la macro « Bandeau de logos » (`LogoStripBlock`) : les six logos
 * de démo, sur le style `logo-strip`. Les réglages ne sont PAS recopiés ici —
 * ils appartiennent au catalogue, sinon la tuile et la vignette du sélecteur
 * divergeraient sans que rien ne le signale. La macro n'apporte que ce qu'un
 * style ne pose jamais : du contenu.
 */
export function logoStripCarouselProps() {
  return {
    ...applyCarouselStyle(carouselDefaultProps(), carouselStyleByKey('logo-strip')),
    slides: DEMO_LOGO_STYLES.map((style, i) => carouselSlideDefaults({
      contentMedia: demoLogo(style, i),
    })),
  };
}
